import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks, Task } from '../../crm-tasks/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getDateRangeFromTimeRange } from '../contexts/DashboardFiltersContext';
import type { TimeRange } from './TimeRangeSelector';
import type { DashboardScope } from '../contexts/DashboardFiltersContext';

export interface PendingTasksProps {
  limit?: number;
  priority?: 'high' | 'all';
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  timeRange?: TimeRange;
  scope?: DashboardScope;
}

const PendingTasks: React.FC<PendingTasksProps> = ({
  limit: limitProp = 5,
  priority = 'all',
  embedded,
  settings,
  timeRange = 'month',
  scope = 'myWork',
}) => {
  const limit = typeof settings?.limit === 'number' ? settings.limit : limitProp;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tasks, isLoading } = useTasks();
  const { colors } = useTheme();
  const cardClass = embedded ? 'min-w-0 flex-1 flex flex-col min-h-0' : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-1' : 'space-y-1';

  const { startDate, endDate } = useMemo(() => getDateRangeFromTimeRange(timeRange), [timeRange]);
  const tasksArray = Array.isArray(tasks) ? tasks : [];
  const pending = tasksArray
    .filter((task: Task) => {
      // Scope: My Work = assigned to current user only
      if (scope === 'myWork' && user?.id != null && task.taskAssignedTo !== user.id) {
        return false;
      }
      // Time range: task due date (or created) within range
      const taskDate = task.taskDate || task.taskCreatedAt;
      if (taskDate && timeRange !== 'all') {
        const d = new Date(taskDate);
        if (d < startDate || d > endDate) return false;
      }
      // Filter by priority if specified
      if (priority === 'high' && (!task.taskPriority || task.taskPriority < 3)) {
        return false;
      }
      // Filter tasks with status "Not Started" or "In Progress"
      const statusDescr = task.taskStatusDescr?.ts_descr?.toLowerCase() || '';
      const isNotStarted = statusDescr.includes('not started');
      const isInProgress = statusDescr.includes('in progress');
      return isNotStarted || isInProgress;
    })
    .sort((a: Task, b: Task) => {
      // Sort by priority (higher first), then by date
      const priorityA = a.taskPriority || 0;
      const priorityB = b.taskPriority || 0;
      if (priorityB !== priorityA) return priorityB - priorityA;
      const dateA = new Date(a.taskDate || a.taskCreatedAt || 0).getTime();
      const dateB = new Date(b.taskDate || b.taskCreatedAt || 0).getTime();
      return dateA - dateB;
    })
    .slice(0, limit);

  const getPriorityColor = (priority?: number) => {
    if (!priority) return 'bg-gray-500';
    if (priority >= 4) return 'bg-red-500';
    if (priority >= 3) return 'bg-orange-500';
    return 'bg-yellow-500';
  };

  const getPriorityLabel = (priority?: number) => {
    if (!priority) return 'Normal';
    if (priority >= 4) return 'Critical';
    if (priority >= 3) return 'High';
    if (priority >= 2) return 'Medium';
    return 'Low';
  };

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-3`}>Pending Tasks</h2>
        <div className="space-y-2 sm:space-y-3">
          {[1, 2, 3].map((item) => (
            <div key={item} className={`flex items-center space-x-3 sm:space-x-4 pb-2 sm:pb-3 ${colors.border} border-b last:border-0`}>
              <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded ${colors.surfaceHover} animate-pulse flex-shrink-0`}></div>
              <div className="flex-1 min-w-0">
                <div className={`h-3 sm:h-4 ${colors.surfaceHover} animate-pulse rounded w-3/4 mb-1 sm:mb-2`}></div>
                <div className={`h-2.5 sm:h-3 ${colors.surfaceHover} animate-pulse rounded w-1/2`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className={`text-base font-semibold ${colors.text}`}>Pending Tasks</h2>
        <button
          onClick={() => navigate('/crm_tasks')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium whitespace-nowrap transition-colors"
        >
          View All
        </button>
      </div>
      <div className={listClass}>
        {pending.length > 0 ? (
          pending.map((task: Task) => {
            const taskDate = task.taskDate ? new Date(task.taskDate) : null;
            const statusDescr = task.taskStatusDescr?.ts_descr?.toLowerCase() || '';
            const isNotCompleted = statusDescr.includes('not started') || statusDescr.includes('in progress');
            const isOverdue = taskDate && taskDate < new Date() && isNotCompleted;
            
            return (
              <div
                key={task.taskId}
                onClick={() => navigate(`/crm_tasks/${task.taskId}`)}
                className={`flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${isOverdue ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}
              >
                <div className="flex-shrink-0 mt-1">
                  <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${getPriorityColor(task.taskPriority)}`} title={getPriorityLabel(task.taskPriority)}></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2 mb-1`}>{task.taskDescription || 'Untitled Task'}</p>
                  {task.taskStatusDescr?.ts_descr && (
                    <p className={`text-[10px] sm:text-xs ${colors.textSecondary} mb-1`}>{task.taskStatusDescr.ts_descr}</p>
                  )}
                  
                  <div className={`flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 sm:mt-2 text-[10px] sm:text-xs ${colors.textMuted}`}>
                    {task.taskAssignedToUser && (
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="truncate">{task.taskAssignedToUser.userDisplayName || task.taskAssignedToUser.userFirstName || 'Unassigned'}</span>
                      </div>
                    )}
                    
                    {task.client?.clientName && (
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="truncate">{task.client.clientName.length > 20 ? task.client.clientName.substring(0, 20) + '...' : task.client.clientName}</span>
                      </div>
                    )}
                    
                    {taskDate && (
                      <div className="flex items-center gap-1">
                        <svg className={`w-3 h-3 flex-shrink-0 ${isOverdue ? 'text-red-500' : colors.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                          {isOverdue ? 'Overdue: ' : ''}{taskDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className={`text-center py-8 ${colors.textMuted}`}>
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <p className="text-sm sm:text-base">No pending tasks</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingTasks;

